import { computed, DestroyRef, effect, inject, Service, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription, timer } from 'rxjs';
import { SseService, SseStatus } from '../core/sse';

/** How long a recovery is announced for before the bar clears itself. */
export const RECONNECTED_NOTICE_MS = 1_000;

/**
 * How long a dropped stream is given to heal before the bar admits to it. An instant failure is
 * often followed by a successful retry within a second or two, and announcing a blip the
 * customer never noticed is noise. Retrying is untouched — only the message waits.
 */
export const CONNECTING_NOTICE_DELAY_MS = 2_000;

/** What the connection bar is saying. `hidden` renders nothing at all. */
export type ConnectionNotice = 'hidden' | 'connecting' | 'connected' | 'disconnected' | 'offline';

/**
 * The bar's wording. These are the Persian lines the desktop/mobile apps show for the same
 * situations — a bar that says the same thing the same way in every app is the point of
 * repeating them here rather than inventing new ones.
 */
const NOTICE_MESSAGES: Record<Exclude<ConnectionNotice, 'hidden'>, string> = {
	connecting: 'در حال اتصال...',
	connected: 'ارتباط برقرار شد.',
	disconnected: 'ممکن است اطلاعات قیمتی بازار بروز نباشد.',
	offline: 'اتصال اینترنت خود را بررسی کنید.'
};

@Service()
export class ConnectionStateService {
	private readonly sseService = inject(SseService);
	private readonly destroyRef = inject(DestroyRef);

	private readonly offline = signal(!navigator.onLine);

	private readonly reconnected = signal(false);

	private readonly connecting = signal(false);

	private dropShown = false;

	private lastRecoveries = 0;

	private reconnectNotice?: Subscription;

	private connectingNotice?: Subscription;

	readonly notice = computed<ConnectionNotice>(() => {
		if (this.offline()) return 'offline';
		if (this.reconnected()) return 'connected';

		switch (this.sseService.status()) {
			case 'disconnected':
				return 'disconnected';
			case 'reconnecting':
				return this.connecting() ? 'connecting' : 'hidden';
			default:
				return 'hidden';
		}
	});

	readonly message = computed(() => {
		const notice = this.notice();
		return notice === 'hidden' ? '' : NOTICE_MESSAGES[notice];
	});

	constructor() {
		this.lastRecoveries = this.sseService.recoveries();

		window.addEventListener('online', this.onOnline);
		window.addEventListener('offline', this.onOffline);

		this.destroyRef.onDestroy(() => {
			window.removeEventListener('online', this.onOnline);
			window.removeEventListener('offline', this.onOffline);
		});

		effect(() => {
			const status = this.sseService.status();
			const recoveries = this.sseService.recoveries();

			untracked(() => this.onSseChanged(status, recoveries));
		});
	}

	private readonly onOnline = (): void => {
		this.offline.set(false);
	};

	private readonly onOffline = (): void => {
		this.offline.set(true);
		this.clearReconnectNotice();
	};

	private onSseChanged(status: SseStatus, recoveries: number): void {
		this.trackConnectingNotice(status);

		if (status === 'disconnected' || status === 'reconnecting') {
			this.clearReconnectNotice();
		}

		if (recoveries > this.lastRecoveries) {
			this.lastRecoveries = recoveries;

			if (status === 'connected' && this.dropShown) {
				this.announceReconnect();
			}
		}

		if (status !== 'disconnected' && status !== 'reconnecting') {
			this.dropShown = false;
		}
	}

	private trackConnectingNotice(status: SseStatus): void {
		if (status === 'disconnected') {
			this.clearConnectingTimer();
			this.connecting.set(false);
			return;
		}

		if (status !== 'reconnecting') {
			this.clearConnectingNotice();
			return;
		}

		if (this.connecting() || this.connectingNotice) {
			return;
		}

		if (this.dropShown) {
			this.connecting.set(true);
			return;
		}

		this.connectingNotice = timer(CONNECTING_NOTICE_DELAY_MS)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this.connectingNotice = undefined;
				this.connecting.set(true);
				this.dropShown = true;
			});
	}

	private clearConnectingTimer(): void {
		this.connectingNotice?.unsubscribe();
		this.connectingNotice = undefined;
	}

	private clearConnectingNotice(): void {
		this.clearConnectingTimer();
		this.connecting.set(false);
	}

	private announceReconnect(): void {
		this.reconnected.set(true);
		this.reconnectNotice?.unsubscribe();
		this.reconnectNotice = timer(RECONNECTED_NOTICE_MS)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.reconnected.set(false));
	}

	private clearReconnectNotice(): void {
		this.reconnectNotice?.unsubscribe();
		this.reconnectNotice = undefined;
		this.reconnected.set(false);
	}
}
